import Decimal from 'decimal.js';

export type NumericParseFailureReason =
  'empty' | 'unsupported-type' | 'invalid-format' | 'non-finite';

export interface NumericParseSuccess {
  readonly ok: true;
  readonly decimal: Decimal;
  readonly normalized: string;
}

export interface NumericParseFailure {
  readonly ok: false;
  readonly reason: NumericParseFailureReason;
  readonly input: unknown;
}

export type NumericParseResult = NumericParseSuccess | NumericParseFailure;

const STANDARD_NUMBER_PATTERN = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:e[+-]?\d+)?$/iu;
const COMMA_GROUPED_PATTERN = /^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d+)?(?:e[+-]?\d+)?$/iu;
const SPACE_GROUPED_PATTERN = /^[+-]?\d{1,3}(?: \d{3})+(?:\.\d+)?(?:e[+-]?\d+)?$/iu;

export function parseNumeric(input: unknown): NumericParseResult {
  if (input instanceof Decimal) {
    if (!input.isFinite()) return { ok: false, reason: 'non-finite', input };
    return { ok: true, decimal: new Decimal(input), normalized: input.toString() };
  }

  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return { ok: false, reason: 'non-finite', input };
    const normalized = String(input);
    return { ok: true, decimal: new Decimal(normalized), normalized };
  }

  if (typeof input === 'bigint') {
    const normalized = input.toString();
    return { ok: true, decimal: new Decimal(normalized), normalized };
  }

  if (input === null || input === undefined) return { ok: false, reason: 'empty', input };
  if (typeof input !== 'string') return { ok: false, reason: 'unsupported-type', input };

  let source = input
    .normalize('NFKC')
    .replace(/[−﹣–—]/gu, '-')
    .replace(/，/gu, ',')
    .replace(/[\u00a0\u2007\u202f]/gu, ' ')
    .trim();
  if (source.length === 0) return { ok: false, reason: 'empty', input };

  let parenthesizedNegative = false;
  if (source.startsWith('(') || source.endsWith(')')) {
    if (!(source.startsWith('(') && source.endsWith(')'))) {
      return { ok: false, reason: 'invalid-format', input };
    }
    source = source.slice(1, -1).trim();
    if (/^[+-]/u.test(source)) return { ok: false, reason: 'invalid-format', input };
    parenthesizedNegative = true;
  }

  source = source.replace(/[\t\r\n]/gu, ' ').replace(/ {2,}/gu, ' ');
  const matchesSupportedFormat =
    STANDARD_NUMBER_PATTERN.test(source) ||
    COMMA_GROUPED_PATTERN.test(source) ||
    SPACE_GROUPED_PATTERN.test(source);
  if (!matchesSupportedFormat) return { ok: false, reason: 'invalid-format', input };

  const unsignedNormalized = source.replace(/[ ,]/gu, '');
  const normalized = parenthesizedNegative ? `-${unsignedNormalized}` : unsignedNormalized;
  try {
    const decimal = new Decimal(normalized);
    if (!decimal.isFinite()) return { ok: false, reason: 'non-finite', input };
    return { ok: true, decimal, normalized: decimal.toString() };
  } catch {
    return { ok: false, reason: 'invalid-format', input };
  }
}

export interface AmountComparison {
  readonly expected: Decimal;
  readonly actual: Decimal;
  readonly difference: Decimal;
  readonly allowedTolerance: Decimal;
  readonly withinTolerance: boolean;
}

export interface DecimalTolerance {
  readonly absolute: string | number | Decimal;
  readonly relative: string | number | Decimal;
}

export function compareAmount(
  quantity: unknown,
  unitPrice: unknown,
  amount: unknown,
  tolerance: DecimalTolerance,
): AmountComparison | null {
  const parsedQuantity = parseNumeric(quantity);
  const parsedUnitPrice = parseNumeric(unitPrice);
  const parsedAmount = parseNumeric(amount);
  if (!parsedQuantity.ok || !parsedUnitPrice.ok || !parsedAmount.ok) return null;

  const expected = parsedQuantity.decimal.mul(parsedUnitPrice.decimal);
  const actual = parsedAmount.decimal;
  const difference = actual.minus(expected).abs();
  const absoluteTolerance = new Decimal(tolerance.absolute).abs();
  const relativeTolerance = expected.abs().mul(new Decimal(tolerance.relative).abs());
  const allowedTolerance = Decimal.max(absoluteTolerance, relativeTolerance);
  return {
    expected,
    actual,
    difference,
    allowedTolerance,
    withinTolerance: difference.lte(allowedTolerance),
  };
}

export function decimalMedian(values: readonly Decimal[]): Decimal | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((left, right) => left.comparedTo(right));
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle] ?? null;
  const left = sorted[middle - 1];
  const right = sorted[middle];
  return left === undefined || right === undefined ? null : left.plus(right).div(2);
}
